import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getArticle } from "@/lib/api";
import { applyPageMeta } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    setChargement(true);
    setErreur(false);
    getArticle(slug)
      .then((data) => {
        setArticle(data);
        applyPageMeta({
          title: `${data.titre} — Fondation HORA`,
          description: data.extrait,
          path: `/blogue/${data.slug}`,
        });
      })
      .catch(() => setErreur(true))
      .finally(() => setChargement(false));
  }, [slug]);

  if (chargement) {
    return (
      <section className="bg-background py-32" data-testid="article-loading">
        <p className="mx-auto max-w-3xl px-6 text-foreground/50">Chargement de l'article…</p>
      </section>
    );
  }

  if (erreur || !article) {
    return (
      <section className="bg-background py-32" data-testid="article-not-found">
        <div className="mx-auto max-w-3xl px-6">
          <h1 className="font-serif text-4xl font-medium">Article introuvable</h1>
          <Link to="/blogue" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary" data-testid="article-back-notfound">
            <ArrowLeft size={16} /> Retour au blogue
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="bg-espresso py-24 text-ivory md:py-32" data-testid="article-hero">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <Link to="/blogue" className="group inline-flex items-center gap-2 text-sm text-ivory/60 transition-colors hover:text-gold" data-testid="article-back-link">
              <ArrowLeft size={16} className="transition-transform duration-200 group-hover:-translate-x-1" /> Retour au blogue
            </Link>
            <div className="mt-10 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.2em]">
              <span className="text-gold" data-testid="article-categorie">{article.categorie}</span>
              <span className="text-ivory/40">{article.date} · {article.lecture} de lecture</span>
            </div>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl" data-testid="article-title">
              {article.titre}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-20 md:py-28" data-testid="article-body">
        <div className="mx-auto max-w-3xl px-6">
          {article.video_path && (
            <Reveal>
              <div className="mb-12 overflow-hidden border border-border bg-espresso" data-testid="article-video">
                <video
                  src={`${BACKEND}/api/media/${article.video_path}`}
                  controls
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  preload="metadata"
                  className="aspect-video w-full"
                />
              </div>
              <p className="mb-10 text-xs text-foreground/40">
                Enregistrement protégé — réservé à la consultation en ligne sur ce site.
              </p>
            </Reveal>
          )}
          {article.contenu.map((p, i) => {
            const titreNiveau2 = p.startsWith("## ");
            const titreNiveau3 = p.startsWith("### ");
            const numerote = p.match(/^(\d+)\.\s+(.+)$/);
            const puce = p.match(/^[*-]\s+(.+)$/);

            if (titreNiveau2 || titreNiveau3) {
              const niveau = titreNiveau2 ? 3 : 4;
              const Balise = titreNiveau2 ? "h2" : "h3";
              return (
                <Reveal key={i} delay={Math.min(i * 0.05, 0.3)}>
                  <Balise className={`${i === 0 ? "" : "mt-14"} border-l-2 border-primary pl-5 font-serif font-semibold leading-snug text-foreground ${titreNiveau2 ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"}`} data-testid={`article-heading-${i}`}>
                    {renderInline(p.slice(niveau))}
                  </Balise>
                </Reveal>
              );
            }

            if (numerote || puce) {
              return (
                <Reveal key={i} delay={Math.min(i * 0.05, 0.3)}>
                  <div className="mt-7 flex gap-4" data-testid={`article-list-item-${i}`}>
                    <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 font-serif text-sm font-semibold text-primary">
                      {numerote ? numerote[1] : "•"}
                    </span>
                    <p className="text-base leading-relaxed text-foreground/75 md:text-lg">
                      {renderInline(numerote ? numerote[2] : puce[1])}
                    </p>
                  </div>
                </Reveal>
              );
            }

            return (
              <Reveal key={i} delay={Math.min(i * 0.05, 0.3)}>
                <p className={`leading-relaxed text-foreground/75 ${i === 0 ? "font-serif text-xl md:text-2xl" : "mt-8 text-base md:text-lg"}`} data-testid={`article-paragraph-${i}`}>
                  {renderInline(p)}
                </p>
              </Reveal>
            );
          })}

          <Reveal className="mt-20">
            <div className="border border-border bg-card p-10" data-testid="article-cta">
              <h2 className="font-serif text-2xl font-semibold md:text-3xl">Envie d'aller plus loin?</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground/60 md:text-base">
                Découvrez nos formations pratiques ou soutenez notre mission pour que ce savoir
                demeure accessible à toutes et à tous.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/formations" className="rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]" data-testid="article-cta-formations">
                  Voir les formations
                </Link>
                <Link to="/don" className="rounded-sm border border-foreground/20 px-6 py-3 text-sm font-semibold transition-colors hover:border-primary hover:text-primary" data-testid="article-cta-don">
                  Faire un don
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
